<script setup>
import { computed, onMounted, ref, nextTick } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';

const store = useStore();

const showAddPopup = ref(false);
const showEditPopup = ref(false);
const showGenderConfig = ref(false);
const editingTemplate = ref(null);
const imageFile = ref(null);
const imagePreview = ref('');

// Track which textarea is active for variable insertion
const activeField = ref('content'); // content | html_content | subject
const contentRef = ref(null);
const htmlContentRef = ref(null);
const subjectRef = ref(null);

const form = ref({
  name: '',
  channel_type: 'all',
  subject: '',
  content: '',
  html_content: '',
  image_url: '',
  category: 'marketing',
  status: 'draft',
});

// Gender title mapping (configurable per account)
const genderMapping = ref({
  male: 'anh',
  female: 'chị',
  unknown: 'anh/chị',
});

const templates = computed(() => store.getters['campaignTemplates/getTemplates']);
const uiFlags = computed(() => store.getters['campaignTemplates/getUIFlags']);

onMounted(async () => {
  await store.dispatch('campaignTemplates/get');
  // Load gender mapping from account settings
  try {
    const account = store.getters['accounts/getAccount'];
    const mapping = account?.custom_attributes?.gender_title_mapping;
    if (mapping && mapping.male) {
      genderMapping.value = { ...mapping };
    }
  } catch { /* use defaults */ }
});

const channelOptions = [
  { label: 'All channels', value: 'all' },
  { label: 'Zalo', value: 'zalo' },
  { label: 'Zalo OA', value: 'zalo_oa' },
  { label: 'WhatsApp', value: 'whatsapp' },
  { label: 'Email', value: 'email' },
  { label: 'Facebook', value: 'facebook' },
  { label: 'SMS', value: 'sms' },
];

const statusOptions = [
  { label: 'Draft', value: 'draft' },
  { label: 'Active', value: 'active' },
  { label: 'Archived', value: 'archived' },
];

const channelSupportsImage = computed(() => {
  return ['all', 'zalo', 'zalo_oa', 'whatsapp', 'facebook'].includes(form.value.channel_type);
});

const isEmailChannel = computed(() => {
  return form.value.channel_type === 'email';
});

// Variable chips with descriptions
const liquidVars = [
  { tag: '{{contact.name}}', label: 'Full Name' },
  { tag: '{{contact.first_name}}', label: 'First Name' },
  { tag: '{{contact.last_name}}', label: 'Last Name' },
  { tag: '{{contact.gender_title}}', label: `Title (${genderMapping.value.male}/${genderMapping.value.female})` },
  { tag: '{{contact.email}}', label: 'Email' },
  { tag: '{{contact.phone_number}}', label: 'Phone' },
  { tag: '{{agent.name}}', label: 'Agent Name' },
  { tag: '{{account.name}}', label: 'Account Name' },
];

// Insert variable at cursor position in active textarea
const insertVariable = (tag) => {
  const fieldMap = {
    content: contentRef,
    html_content: htmlContentRef,
    subject: subjectRef,
  };
  const textareaRef = fieldMap[activeField.value];
  const textarea = textareaRef?.value;

  if (textarea) {
    const start = textarea.selectionStart || 0;
    const end = textarea.selectionEnd || 0;
    const currentValue = form.value[activeField.value] || '';
    form.value[activeField.value] =
      currentValue.substring(0, start) + tag + currentValue.substring(end);

    // Restore cursor position after tag
    nextTick(() => {
      textarea.focus();
      const newPos = start + tag.length;
      textarea.setSelectionRange(newPos, newPos);
    });
  } else {
    // Fallback: append to active field
    form.value[activeField.value] = (form.value[activeField.value] || '') + tag;
  }
};

const setActiveField = (field) => {
  activeField.value = field;
};

// Save gender title mapping to account custom_attributes
const saveGenderMapping = async () => {
  try {
    await store.dispatch('accounts/update', {
      custom_attributes: {
        ...(store.getters['accounts/getAccount']?.custom_attributes || {}),
        gender_title_mapping: genderMapping.value,
      },
    });
    useAlert('Gender title mapping saved');
    showGenderConfig.value = false;
  } catch (e) {
    useAlert(e.message || 'Failed to save');
  }
};

const resetForm = () => {
  form.value = {
    name: '', channel_type: 'all', subject: '', content: '', html_content: '',
    image_url: '', category: 'marketing', status: 'draft',
  };
  imageFile.value = null;
  imagePreview.value = '';
  editingTemplate.value = null;
  showAddPopup.value = false;
  showEditPopup.value = false;
  activeField.value = 'content';
};

const openAddPopup = () => { resetForm(); showAddPopup.value = true; };

const openEditPopup = (template) => {
  editingTemplate.value = template;
  form.value = { ...template };
  imagePreview.value = template.image_url || '';
  showEditPopup.value = true;
};

const onImageSelected = (event) => {
  const file = event.target.files[0];
  if (!file) return;
  const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
  if (!allowedTypes.includes(file.type)) { useAlert('Only JPEG, PNG, GIF, WebP supported'); return; }
  if (file.size > 10 * 1024 * 1024) { useAlert('Image must be < 10MB'); return; }
  imageFile.value = file;
  imagePreview.value = URL.createObjectURL(file);
};

const removeImage = () => {
  imageFile.value = null;
  imagePreview.value = '';
  form.value.image_url = '';
};

const saveTemplate = async () => {
  try {
    let payload;
    if (imageFile.value) {
      payload = new FormData();
      Object.keys(form.value).forEach(key => {
        if (form.value[key] !== null && form.value[key] !== undefined) {
          payload.append(`campaign_template[${key}]`, form.value[key]);
        }
      });
      payload.append('campaign_template[image]', imageFile.value);
    } else {
      payload = form.value;
    }

    if (editingTemplate.value) {
      await store.dispatch('campaignTemplates/update', {
        id: editingTemplate.value.id,
        ...(!imageFile.value ? form.value : {}),
        ...(imageFile.value ? { formData: payload } : {}),
      });
      useAlert('Template updated successfully');
    } else {
      await store.dispatch('campaignTemplates/create', payload);
      useAlert('Template created successfully');
    }
    resetForm();
  } catch (e) {
    useAlert(e.message || 'An error occurred');
  }
};

const deleteTemplate = async (id) => {
  try {
    await store.dispatch('campaignTemplates/delete', id);
    useAlert('Template deleted');
  } catch (e) {
    useAlert(e.message || 'An error occurred');
  }
};

const tableHeaders = ['Name', 'Channel', 'Status', 'Actions'];

const channelLabel = (type) => {
  const found = channelOptions.find(c => c.value === type);
  return found ? found.label : type || 'All';
};
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    loading-message="Loading templates..."
    :no-records-found="!templates.length"
    no-records-message="No campaign templates found. Create one to get started."
  >
    <template #header>
      <BaseSettingsHeader
        title="Campaign Templates"
        description="Manage reusable message templates for marketing campaigns. Click a variable chip to insert it at cursor position."
      >
        <template #actions>
          <div class="flex gap-2">
            <Button label="Gender Title Config" size="sm" faded slate @click="showGenderConfig = !showGenderConfig" />
            <Button label="Add Template" size="sm" @click="openAddPopup" />
          </div>
        </template>
      </BaseSettingsHeader>
    </template>

    <template #preBody>
      <!-- Gender Title Config Panel -->
      <div v-if="showGenderConfig"
        class="rounded-xl border border-n-weak p-4 mb-4"
      >
        <p class="text-heading-3 text-n-slate-12 mb-3">
          Gender Title Mapping
        </p>
        <p class="text-body-main text-n-slate-11 mb-3">
          Configure how the gender_title variable renders for each gender. This applies to all templates account-wide.
        </p>
        <div class="flex gap-4 items-end">
          <div class="flex-1">
            <label>
              Male
              <input v-model="genderMapping.male" type="text" placeholder="anh / Mr." />
            </label>
          </div>
          <div class="flex-1">
            <label>
              Female
              <input v-model="genderMapping.female" type="text" placeholder="chị / Ms." />
            </label>
          </div>
          <div class="flex-1">
            <label>
              Unknown / Default
              <input v-model="genderMapping.unknown" type="text" placeholder="anh/chị / Dear" />
            </label>
          </div>
          <Button label="Save Mapping" size="sm" @click="saveGenderMapping" />
        </div>
      </div>
    </template>

    <template #body>
      <BaseTable :headers="tableHeaders" :items="templates">
        <template #row="{ items }">
          <BaseTableRow v-for="tpl in items" :key="tpl.id" :item="tpl">
            <template #default>
              <BaseTableCell class="max-w-0">
                <div class="flex items-center gap-3 min-w-0">
                  <img v-if="tpl.image_url" :src="tpl.image_url"
                    class="w-10 h-10 rounded-lg object-cover flex-shrink-0 border border-n-weak" alt="" />
                  <div class="flex flex-col gap-1 min-w-0">
                    <span class="text-heading-3 text-n-slate-12 truncate block">{{ tpl.name }}</span>
                    <p v-if="tpl.subject" class="text-body-main text-n-slate-11 mb-0 truncate">
                      Subject: {{ tpl.subject }}
                    </p>
                    <p class="text-body-main text-n-slate-11 line-clamp-2 mb-0">{{ tpl.content }}</p>
                  </div>
                </div>
              </BaseTableCell>

              <BaseTableCell class="w-28">
                <span class="text-body-main text-n-slate-11">{{ channelLabel(tpl.channel_type) }}</span>
              </BaseTableCell>

              <BaseTableCell class="w-24">
                <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                  :class="{
                    'bg-n-teal-3 text-n-teal-11': tpl.status === 'active',
                    'bg-n-amber-3 text-n-amber-11': tpl.status === 'draft',
                    'bg-n-slate-3 text-n-slate-11': tpl.status === 'archived',
                  }">
                  {{ tpl.status }}
                </span>
              </BaseTableCell>

              <BaseTableCell align="end" class="w-24">
                <div class="flex gap-3 justify-end flex-shrink-0">
                  <Button v-tooltip.top="'Edit'" icon="i-woot-edit-pen" slate sm @click="openEditPopup(tpl)" />
                  <Button v-tooltip.top="'Delete'" icon="i-woot-bin" slate sm
                    class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                    @click="deleteTemplate(tpl.id)" />
                </div>
              </BaseTableCell>
            </template>
          </BaseTableRow>
        </template>
      </BaseTable>
    </template>

    <!-- Shared form content as a component-like template -->
    <!-- Add Modal -->
    <woot-modal v-model:show="showAddPopup" :on-close="resetForm">
      <div class="flex flex-col h-auto overflow-auto">
        <woot-modal-header
          header-title="Add Campaign Template"
          header-content="Create a reusable message template. Click variable chips below to insert into active field."
        />
        <form class="flex flex-col w-full" @submit.prevent="saveTemplate">
          <div class="flex gap-4">
            <div class="flex-1">
              <label>Name <input v-model="form.name" type="text" placeholder="e.g. Welcome Message" /></label>
            </div>
            <div class="flex-1">
              <label>Channel
                <select v-model="form.channel_type" class="mb-0">
                  <option v-for="opt in channelOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                </select>
              </label>
            </div>
          </div>

          <!-- Variable Chips -->
          <div class="mt-2 mb-1">
            <p class="text-xs text-n-slate-11 mb-1.5">Click to insert variable into active field:</p>
            <div class="flex flex-wrap gap-1.5">
              <button v-for="v in liquidVars" :key="v.tag" type="button"
                class="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium transition-colors cursor-pointer
                  bg-n-blue-2 text-n-blue-11 border border-n-blue-5 hover:bg-n-blue-3 hover:border-n-blue-7 active:bg-n-blue-4"
                @click="insertVariable(v.tag)"
              >
                <span class="font-mono">{{ v.tag }}</span>
                <span class="text-n-slate-11 font-normal">({{ v.label }})</span>
              </button>
            </div>
          </div>

          <!-- Email: Subject -->
          <div v-if="isEmailChannel" class="w-full">
            <label>Email Subject
              <input ref="subjectRef" v-model="form.subject" type="text"
                placeholder="e.g. Welcome {{contact.gender_title}} {{contact.name}}"
                @focus="setActiveField('subject')" />
            </label>
          </div>

          <!-- Message Content -->
          <div class="w-full">
            <label>{{ isEmailChannel ? 'Plain Text (fallback)' : 'Message Content' }}
              <textarea ref="contentRef" v-model="form.content" rows="4"
                :placeholder="isEmailChannel ? 'Plain text fallback...' : 'Hello {{contact.gender_title}} {{contact.name}}, ...'"
                @focus="setActiveField('content')" />
            </label>
          </div>

          <!-- Email: HTML -->
          <div v-if="isEmailChannel" class="w-full">
            <label>HTML Content
              <textarea ref="htmlContentRef" v-model="form.html_content" rows="6"
                placeholder="<html>...</html>"
                @focus="setActiveField('html_content')" />
            </label>
          </div>

          <!-- Image Upload -->
          <div v-if="channelSupportsImage" class="w-full">
            <label class="block mb-1">Attachment Image</label>
            <div class="flex items-start gap-4">
              <div v-if="imagePreview" class="relative flex-shrink-0">
                <img :src="imagePreview" class="w-24 h-24 rounded-lg object-cover border border-n-weak" alt="preview" />
                <button type="button"
                  class="absolute -top-2 -right-2 w-5 h-5 bg-n-ruby-9 text-white rounded-full text-xs flex items-center justify-center hover:bg-n-ruby-10"
                  @click="removeImage">×</button>
              </div>
              <div class="flex flex-col gap-2 flex-1">
                <input type="file" accept="image/jpeg,image/png,image/gif,image/webp" @change="onImageSelected" />
                <p class="text-xs text-n-slate-11 mb-0">JPEG, PNG, GIF, WebP. Max 10MB.</p>
                <div class="flex items-center gap-2">
                  <span class="text-xs text-n-slate-11">or URL:</span>
                  <input v-model="form.image_url" type="text" placeholder="https://..." class="text-sm flex-1" />
                </div>
              </div>
            </div>
          </div>

          <!-- Status + Category -->
          <div class="flex gap-4 mt-2">
            <div class="flex-1">
              <label>Status
                <select v-model="form.status" class="mb-0">
                  <option v-for="opt in statusOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                </select>
              </label>
            </div>
            <div class="flex-1">
              <label>Category
                <select v-model="form.category" class="mb-0">
                  <option value="marketing">Marketing</option>
                  <option value="transactional">Transactional</option>
                  <option value="greeting">Greeting</option>
                </select>
              </label>
            </div>
          </div>

          <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
            <Button faded slate type="reset" label="Cancel" @click.prevent="resetForm" />
            <Button type="submit" label="Save Template" />
          </div>
        </form>
      </div>
    </woot-modal>

    <!-- Edit Modal -->
    <woot-modal v-model:show="showEditPopup" :on-close="resetForm">
      <div class="flex flex-col h-auto overflow-auto">
        <woot-modal-header header-title="Edit Campaign Template"
          header-content="Click variable chips to insert into the active text field." />
        <form class="flex flex-col w-full" @submit.prevent="saveTemplate">
          <div class="flex gap-4">
            <div class="flex-1">
              <label>Name <input v-model="form.name" type="text" /></label>
            </div>
            <div class="flex-1">
              <label>Channel
                <select v-model="form.channel_type" class="mb-0">
                  <option v-for="opt in channelOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                </select>
              </label>
            </div>
          </div>

          <!-- Variable Chips -->
          <div class="mt-2 mb-1">
            <p class="text-xs text-n-slate-11 mb-1.5">Click to insert:</p>
            <div class="flex flex-wrap gap-1.5">
              <button v-for="v in liquidVars" :key="v.tag" type="button"
                class="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium transition-colors cursor-pointer
                  bg-n-blue-2 text-n-blue-11 border border-n-blue-5 hover:bg-n-blue-3 hover:border-n-blue-7 active:bg-n-blue-4"
                @click="insertVariable(v.tag)"
              >
                <span class="font-mono">{{ v.tag }}</span>
                <span class="text-n-slate-11 font-normal">({{ v.label }})</span>
              </button>
            </div>
          </div>

          <div v-if="isEmailChannel" class="w-full">
            <label>Email Subject
              <input ref="subjectRef" v-model="form.subject" type="text" @focus="setActiveField('subject')" />
            </label>
          </div>

          <div class="w-full">
            <label>{{ isEmailChannel ? 'Plain Text (fallback)' : 'Message Content' }}
              <textarea ref="contentRef" v-model="form.content" rows="4" @focus="setActiveField('content')" />
            </label>
          </div>

          <div v-if="isEmailChannel" class="w-full">
            <label>HTML Content
              <textarea ref="htmlContentRef" v-model="form.html_content" rows="6" @focus="setActiveField('html_content')" />
            </label>
          </div>

          <div v-if="channelSupportsImage" class="w-full">
            <label class="block mb-1">Attachment Image</label>
            <div class="flex items-start gap-4">
              <div v-if="imagePreview" class="relative flex-shrink-0">
                <img :src="imagePreview" class="w-24 h-24 rounded-lg object-cover border border-n-weak" alt="preview" />
                <button type="button"
                  class="absolute -top-2 -right-2 w-5 h-5 bg-n-ruby-9 text-white rounded-full text-xs flex items-center justify-center hover:bg-n-ruby-10"
                  @click="removeImage">×</button>
              </div>
              <div class="flex flex-col gap-2 flex-1">
                <input type="file" accept="image/jpeg,image/png,image/gif,image/webp" @change="onImageSelected" />
                <p class="text-xs text-n-slate-11 mb-0">Upload or paste URL</p>
                <div class="flex items-center gap-2">
                  <span class="text-xs text-n-slate-11">URL:</span>
                  <input v-model="form.image_url" type="text" placeholder="https://..." class="text-sm flex-1" />
                </div>
              </div>
            </div>
          </div>

          <div class="flex gap-4 mt-2">
            <div class="flex-1">
              <label>Status
                <select v-model="form.status" class="mb-0">
                  <option v-for="opt in statusOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                </select>
              </label>
            </div>
            <div class="flex-1">
              <label>Category
                <select v-model="form.category" class="mb-0">
                  <option value="marketing">Marketing</option>
                  <option value="transactional">Transactional</option>
                  <option value="greeting">Greeting</option>
                </select>
              </label>
            </div>
          </div>

          <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
            <Button faded slate type="reset" label="Cancel" @click.prevent="resetForm" />
            <Button type="submit" label="Update Template" />
          </div>
        </form>
      </div>
    </woot-modal>
  </SettingsLayout>
</template>
